<template>
  <div class="mapping-rules-change module-rules-form-changes__item">
    <div class="mapping-rules-change__data">
      <span v-html="itemData" />
      <div class="mapping-rules-change__data__description">
        {{ item.description }}
      </div>
      <div
        v-if="item.mapping_status"
        class="mapping-rules-change__data__mapping-status"
      >
        <span
          class="mapping-rules-change__mapping-status"
          :class="`mapping-rules-change__mapping-status--${getMappingStatusColor(item.mapping_status)}`"
        >
          {{ getMappingStatusLabel(item.mapping_status) }}
        </span>
        <div
          v-if="item.mapping_status === MappingStatus.FAILED && item.mapping_message"
          class="mapping-rules-change__mapping-message"
        >
          <span class="mapping-rules-change__mapping-message-label">Reason:</span>
          {{ item.mapping_message }}
        </div>
      </div>
    </div>
    <div class="mapping-rules-change__list">
      <div>Type: <span class="mapping-rules-change__list-value">{{ type }}</span></div>
      <div>
        <span
          v-for="(change, index) in changes"
          :key="index"
        >{{ +index ? ', ' : '' }}
          <span
            v-for="(entry, eIndex) in change"
            :key="eIndex"
            :class="getEntryClass(entry.key)"
          >
            {{ eIndex ? ' ' : '' }}{{ entry.value }}
          </span>
        </span>
      </div>
      <div>Description: <span class="mapping-rules-change__list-value">{{ description }}</span></div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed } from 'vue'
import DOMPurify from 'dompurify'

import {
  LinkedDataTransaction,
  MappingRuleAction,
  MappingRuleActionAccount,
  MappingStatus,
  TransactionClass,
} from '@types'

import { numberFormat } from '@/helpers/numbers'
import { getMappingStatusLabel, getMappingStatusColor } from '@/store/linkedData/utils/helpers'

import { getTransactionString } from '@/views/Data/Transactions/components/utils/helpers'
import { get } from 'lodash'

type Props = {
  item: LinkedDataTransaction
  actions: MappingRuleAction[]
  jsonData?: Record<string, any>
}

const props = defineProps<Props>()

const changes = computed(() => {
  const action = props.actions?.[0]
  if (!action) return []
  const date = getJSONData(action.date?.value)
  const transaction = new TransactionClass({
    type: action.transaction_type,
    date,
    entries: action.entries?.map(entry => {
      const account_id = (entry.account as MappingRuleActionAccount)?.useContextAccount ? 'THIS' : 'NONE'
      const amount = getJSONData(entry.amount?.value)
      const asset_id = getJSONData(entry.asset?.value)
      return {
        account_id,
        amount,
        asset_id,
      }
    }),
  })
  return getTransactionString(transaction.get(), transaction.settings, value => {
    return value
  })
})

const itemData = computed(() => {
  const { connector_name, account_name, account_number, type, amount } =
    props.item
  const str = [
    connector_name,
    account_name,
    account_number,
    type,
    `<span class="number-node">${numberFormat(amount)}</span>`
  ].join(' ')
  return DOMPurify.sanitize(str)
})

const type = computed(() => props.actions?.[0]?.transaction_type)

const description = computed(() => getJSONData(props.actions?.[0]?.description?.value))

const getJSONData = (value?: string) => {
  return props.jsonData && value ? get(props.jsonData, value) : value
}

const getEntryClass = (key?: string) => {
  if (!key) return
  const result = ['mapping-rules-change__list-value']
  if (key.startsWith('amount')) {
    result.push(`mapping-rules-change__list-value${key.replace('amount', '')}`)
  }
  return result
}
</script>

<style>
.mapping-rules-change {
  @apply grid grid-cols-1 sm:grid-cols-2;
  @apply gap-2;
  @apply text-sm sm:text-xs;

  &__data {
    @apply text-gray-950 dark:text-gray-50;

    &__description {
      @apply text-gray-400 dark:text-gray-500;
    }

    &__mapping-status {
      @apply mt-2;
    }
  }

  &__list-value {
    @apply text-gray-950 dark:text-gray-50;
  }

  &__list-value--positive {
    @apply text-green-600 dark:text-green-500;
  }

  &__list-value--negative {
    @apply text-red-500 dark:text-red-400;
  }

  &__mapping-message {
    @apply mt-1;
    @apply text-[11px] leading-snug;
    @apply text-gray-500 dark:text-gray-400;

    &-label {
      @apply font-medium;
      @apply text-gray-600 dark:text-gray-300;
    }
  }

  &__mapping-status {
    @apply inline-flex items-center;
    @apply px-2 py-1;
    @apply rounded-full;
    @apply text-xs font-medium;
    @apply border;

    &--success {
      @apply bg-green-50 text-green-700 border-green-200;
      @apply dark:bg-green-900/20 dark:text-green-400 dark:border-green-800;
    }

    &--error {
      @apply bg-red-50 text-red-700 border-red-200;
      @apply dark:bg-red-900/20 dark:text-red-400 dark:border-red-800;
    }

    &--warning {
      @apply bg-yellow-50 text-yellow-700 border-yellow-200;
      @apply dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800;
    }

    &--gray {
      @apply bg-gray-50 text-gray-700 border-gray-200;
      @apply dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-800;
    }
  }
}
</style>
